import { useEffect } from 'react';

/** The longest a Print button waits for images before printing anyway (a slow photo must not block it). */
const IMAGE_WAIT_TIMEOUT_MS = 4000;

/**
 * The images a printed itinerary (`[data-print-root]`) shows: one per item. Skips the photos behind
 * the visible one in each item's carousel, which are mounted `aria-hidden` for the next fade and
 * never print, so a printout doesn't wait on whole galleries.
 */
function printedImages(): HTMLImageElement[] {
  return [...document.querySelectorAll<HTMLImageElement>('[data-print-root] img')].filter(
    (image) => !image.closest('[aria-hidden="true"]'),
  );
}

function printableLazyImages(): HTMLImageElement[] {
  return printedImages().filter((image) => image.getAttribute('loading') === 'lazy');
}

/**
 * Starts loading the printed itinerary's lazy images. On screen images stay lazy; this runs
 * only on the way to printing, because a lazy image below the fold has never been fetched, and a
 * printout made then has holes where the photos should be. Switching `loading` to `eager` starts the
 * fetch.
 */
export function primeImagesForPrint(): void {
  for (const image of printableLazyImages()) image.setAttribute('loading', 'eager');
}

/** Resolves once every printed image has loaded or failed, or after `timeoutMs`. */
async function imagesSettled(timeoutMs: number): Promise<void> {
  const pending = printedImages().filter((image) => !image.complete);
  if (pending.length === 0) return;
  const settled = Promise.all(
    pending.map(async (image) => {
      await new Promise<void>((resolve) => {
        image.addEventListener('load', () => resolve(), { once: true });
        image.addEventListener('error', () => resolve(), { once: true });
      });
    }),
  );
  await Promise.race([settled, new Promise<void>((resolve) => setTimeout(resolve, timeoutMs))]);
}

/**
 * Opens the browser's print dialog from a Print button, after loading the itinerary's lazy images.
 *
 * Safari defers `window.print()` until the page has no network requests in flight, and this app keeps
 * long-lived ones open (Firestore listeners, map tiles), so in Safari the button did nothing; Cmd+P
 * still worked because it goes through the browser menu, not the page. Safari's
 * `document.execCommand('print')` prints immediately. Chrome and Firefox don't support that command
 * (it returns false), so they fall back to `window.print()`.
 */
export async function printPage({
  imageTimeoutMs = IMAGE_WAIT_TIMEOUT_MS,
}: { imageTimeoutMs?: number } = {}): Promise<void> {
  // Repeat until no lazy image is left: a photo that fails steps `AppImage` to its next candidate,
  // which mounts as a fresh lazy `<img>` after the first pass.
  const deadline = Date.now() + imageTimeoutMs;
  do {
    primeImagesForPrint();
    await imagesSettled(Math.max(0, deadline - Date.now()));
    // Let React commit any fallback image an error just scheduled.
    await new Promise<void>((resolve) => setTimeout(resolve, 0));
  } while (printableLazyImages().length > 0 && Date.now() < deadline);
  try {
    // `execCommand` is deprecated, but it is the only immediate print in Safari.
    if (document.execCommand('print', false)) return;
  } catch {
    // Unsupported here; fall through to the standard API.
  }
  window.print();
}

/**
 * Loads the itinerary's lazy images when the browser starts printing by other means (Cmd+P, the File
 * menu). Best effort: browsers don't wait for page scripts at that point, but it gives the images a
 * head start, and Chrome and Firefox already load lazy images for print themselves.
 */
export function usePrimeImagesBeforePrint(): void {
  useEffect(() => {
    window.addEventListener('beforeprint', primeImagesForPrint);
    return () => window.removeEventListener('beforeprint', primeImagesForPrint);
  }, []);
}
